import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { IOfferWithScheduleState } from "@/data/model/Offer";
import type { IOfferSchedulePreview } from "@/data/model/OfferSchedule";

import { translate } from "@/shared/localization/language";
import { createEmptyDiv, createVueComponent } from "@/shared/utils/vue";

import { i18n } from "@/portal/Trans";
import OfferRoomActivationModal from "@/portal/components/modal/OfferRoomActivationModal.vue";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { portalEvents } from "@/portal/events/portalEvents";
import { OfferScheduleWindow } from "@/portal/modal/OfferScheduleWindow";
import { PortalFormat } from "@/portal/util/PortalFormat";
import { UiNotification } from "@/portal/ui/Notification";
import { logError } from "@/shared/logging/errors";

export class OfferTableController {
    private $element: JQuery;
    private dao: CatalogDao;
    private dataTable: DataTable<IOfferWithScheduleState>;
    private search: TableFilter;

    private scheduleWindow?: OfferScheduleWindow;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<CatalogDao>(CatalogDao);

        const tableConfig: IDataTableConfig<IOfferWithScheduleState> = {
            columnMapping: [
                {
                    name: i18n("general.fields.designation"),
                    property: "name",
                    styles: { width: "28%" },
                },
                {
                    name: i18n("offerSchedule.fields.currentActive"),
                    property: "currentPlanning",
                    sortable: false,
                    styles: { width: "30%" },
                    formatter: (m: IOfferWithScheduleState, currentPlanning: IOfferSchedulePreview[]) => {
                        let o = "";
                        const displayedForAllUseCasesRooms: string[] = [];
                        for (const planning of currentPlanning) {
                            let useCaseName = translate(planning.orderUseCaseName);
                            if (planning.isForAllUseCases) {
                                if (displayedForAllUseCasesRooms.includes(planning.roomId)) {
                                    continue;
                                }
                                displayedForAllUseCasesRooms.push(planning.roomId);
                                useCaseName = i18n("offerSchedule.allOrderApplications");
                            }

                            o +=
                                planning.roomName +
                                " - " +
                                useCaseName +
                                " - " +
                                PortalFormat.shiftDailyRangeToString(planning.start, planning.end) +
                                "<br />";
                        }

                        return o;
                    },
                },
                {
                    name: i18n("general.fields.internalNote"),
                    property: "internalNote",
                    styles: { width: "20%" },
                },
                {
                    name: "",
                    property: "overruledPlanning",
                    sortable: false,
                    styles: { width: "95px", "text-align": "right" },
                    formatter: (offerWithScheduleState: IOfferWithScheduleState) => {
                        if (offerWithScheduleState.overruledPlanning) {
                            return $(
                                `<span><a class="btn btn-clean btn-sm btn-text-danger"">${i18n("modal.offerRoomActivation.reset")}</span>`
                            ).click((e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                
                                this.resetPlanning(offerWithScheduleState.id);
                                return false;
                            });
                        } else {
                            return null;
                        }
                    }
                },
                {
                    name: "",
                    property: "isActive",
                    sortable: false,
                    styles: { width: "100px", "text-align": "right" },
                    formatter: (offerWithScheduleState) => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean">' + i18n("offer.activateDeactivate") + "</span>"
                        ).click((e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            this.showOfferRoomActivationModal(offerWithScheduleState);
                            
                            return false;
                        });
                    },
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "60px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
            defaultSortAsc: true,
            locationDependend: true,
            defaultSortProperty: "name",
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.dataTable = new DataTable<IOfferWithScheduleState>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao.offerWithScheduleDataSource,
            tableConfig
        );
        this.dataTable.onClick(
            (m) => {
                location.href = "/offer/" + m.id;
            },
            (m) => {
                window.open("/offer/" + m.id);
            }
        );

        this.dataTable.listenTo(this.search);

        portalEvents.OnLocationChanged.add(() => {
            this.dataTable.loadData(true);
        }, this);

        element.find("#previewbtn").click((e) => {
            e.preventDefault();
            this.showPlanningPreview();
        });
    }

    private showPlanningPreview() {
        if (this.scheduleWindow == undefined) this.scheduleWindow = new OfferScheduleWindow();

        this.scheduleWindow.show();
    }
    
    private async resetPlanning(offerId: string) {

        try {
            await this.dao.resetOfferActivations(offerId);
            UiNotification.showSuccess(i18n("modal.offerRoomActivation.sucessfulReset"));
            await this.dataTable.loadData(false)
        } catch (ex) {
            logError(ex);
            UiNotification.showException(i18n("modal.offerRoomActivation.failedReset"));
        }
    }

    private showOfferRoomActivationModal(offerWithScheduleState: IOfferWithScheduleState) {
        const appContainerElement = createEmptyDiv();
        const props = {
            offerWithScheduleState: offerWithScheduleState,
            onChanged: () => this.dataTable.loadData(false),
            onHidden: () => appContainerElement.remove(),
        };

        const result = createVueComponent(OfferRoomActivationModal, props, appContainerElement);
        const modal = result.mountedComponent;
        modal.show();
    }
}
